import { Component, OnInit, AfterViewInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { AuthModalService } from "src/app/Services/auth-modal.service";
import {
  PlanConfigModel,
  SubscriptionService,
} from "src/app/Services/subscription.service";
import {
  formatViewOverageRate,
  formatViewsLimit,
  getPlanLimits,
  mergePlanConfig,
  starterCoinsFeature,
  starterGuideFeature,
  starterViewsFeature,
  growthCoinsFeature,
  growthGuideFeature,
  growthViewsFeature,
} from "src/app/shared/utils/plan-config.util";
import { updateLoggedInUser } from "src/app/shared/utils/plan.util";
import { loadPayPalSdk } from "src/app/shared/utils/paypal-sdk.util";
import { getTrialConfig } from "src/app/shared/utils/plan-config.util";
import { formatTrialEndDate } from "src/app/shared/utils/trial.util";
import { TrialStateService } from "src/app/Services/trial-state.service";
import { TrialStatusModel } from "src/app/Services/subscription.service";
import {
  clearCoinPurchaseReturnUrl,
  resolveCoinPurchaseReturnUrl,
  setCoinPurchaseReturnUrl,
} from "src/app/shared/utils/coin-purchase-return.util";
import { environment } from "src/environments/environment";

declare var paypal: any;

type CheckoutPlan = "starter" | "growth";
type CheckoutBilling = "monthly" | "annual";

interface PlanBenefit {
  icon: string;
  label: string;
  detail: string;
}

interface PlanCard {
  title: string;
  description: string;
  monthlyPrice: number;
  annualMonthlyPrice: number;
  annualLabel: string;
  features: string[];
  buttonLabel: string;
  highlighted?: boolean;
}

@Component({
  selector: "app-subscriptions",
  templateUrl: "./subscriptions.component.html",
  styleUrls: ["./subscriptions.component.scss"],
})
export class SubscriptionsComponent implements OnInit, AfterViewInit, OnDestroy {
  isYearlyBilling = false;
  selectedPlan: CheckoutPlan = "starter";
  selectedBilling: CheckoutBilling = "monthly";
  selectedPlanId = "";
  userEmail = "";
  returnUrl: string | null = null;
  planConfig: PlanConfigModel = mergePlanConfig();

  /** Stable references — never recreate in getters (avoids CD + Translate freezes). */
  planCards: Record<CheckoutPlan, PlanCard> = this.buildPlanCards();
  planBenefits: Record<CheckoutPlan, PlanBenefit[]> = this.buildPlanBenefits();
  activePlanBenefits: PlanBenefit[] = this.planBenefits.starter;

  readonly paypalClientId =
    environment.paypalClientId ||
    "AVk8ODiI6Nh105imMWm_0NFp9mz6ofDjiA2Di3Usq2gaeZ66880LYfgBwjyGjuxnm_M1vsozkp0T2VXg";
  readonly planMappings: Record<string, string> = {
    starter_monthly:
      environment.paypalPlans?.starterMonthly || "P-9VU70762PU6215539ND7DKVI",
    starter_annual:
      environment.paypalPlans?.starterAnnual || "P-9VU70762PU6215539ND7DKVI",
    growth_monthly:
      environment.paypalPlans?.growthMonthly || "P-9VU70762PU6215539ND7DKVI",
    growth_annual:
      environment.paypalPlans?.growthAnnual || "P-9VU70762PU6215539ND7DKVI",
  };
  /** Starter plans with a $0 TRIAL billing cycle; empty until created in the PayPal dashboard. */
  readonly trialPlanMappings: Record<string, string> = {
    starter_monthly: (environment.paypalPlans as any)?.starterMonthlyTrial || "",
    starter_annual: (environment.paypalPlans as any)?.starterAnnualTrial || "",
  };

  /** Requested via ?trial=1 on the Starter plan; cleared when the user isn't eligible. */
  trialRequested = false;
  trialEligible = true;
  trialConsent = false;
  trialStatus: TrialStatusModel | null = null;
  manageTrial = false;
  cancellingTrial = false;

  private readonly paypalContainerId = "paypal-button-container-checkout";
  private paypalReady = false;
  private checkoutAuthorized = false;
  private destroyed = false;
  private queryParamsSub: Subscription | null = null;
  private paypalButtonsInstance: {
    close: () => Promise<void>;
    render: (selector: string) => Promise<void>;
  } | null = null;
  private paypalInitTimer: ReturnType<typeof setTimeout> | null = null;
  private paypalScriptTimer: ReturnType<typeof setTimeout> | null = null;
  private paypalInitGeneration = 0;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    private authModalService: AuthModalService,
    private toastr: ToastrService,
    private trialStateService: TrialStateService
  ) {}

  /** UI + £0 due today when the user asked for a Starter trial and is still eligible. */
  get isTrialCheckout(): boolean {
    return (
      this.trialRequested &&
      this.trialEligible &&
      this.selectedPlan === "starter"
    );
  }

  /** True when a dedicated PayPal plan with a $0 TRIAL cycle is configured. */
  get hasTrialPayPalPlan(): boolean {
    return !!this.trialPlanMappings[`starter_${this.selectedBilling}`];
  }

  get trialDays(): number {
    return getTrialConfig(this.planConfig).days;
  }

  get trialMaxGuides(): number {
    return getTrialConfig(this.planConfig).maxGuides;
  }

  get trialAllowsAi(): boolean {
    return getTrialConfig(this.planConfig).allowAi;
  }

  get trialAllowsGuidePacks(): boolean {
    return getTrialConfig(this.planConfig).allowGuidePacks;
  }

  /** Shown in place of full Starter benefits while starting a free trial. */
  get trialUsageRules(): PlanBenefit[] {
    const trial = getTrialConfig(this.planConfig);
    const guideLabel = `${trial.maxGuides} guide${trial.maxGuides === 1 ? "" : "s"} during the trial`;
    return [
      {
        icon: "money_off",
        label: "£0 due today",
        detail: `No charge until ${this.trialChargeDate}. Add a payment method now for after the trial.`,
      },
      {
        icon: "schedule",
        label: `${trial.days}-day free trial`,
        detail: "Your trial starts as soon as you approve PayPal. Cancel before it ends and you won't be billed.",
      },
      {
        icon: "menu_book",
        label: guideLabel,
        detail: "Manual creation only — publish within this limit while the trial is active.",
      },
      {
        icon: trial.allowAi ? "auto_awesome" : "block",
        label: trial.allowAi ? "AI creation included" : "No AI creation during trial",
        detail: trial.allowAi
          ? "Smart Create and AI features are available for the trial period."
          : "Smart Create, AI coins and AI imports unlock when Starter billing starts.",
      },
      {
        icon: trial.allowGuidePacks ? "inventory_2" : "block",
        label: trial.allowGuidePacks ? "Guide packs allowed" : "No guide packs during trial",
        detail: trial.allowGuidePacks
          ? "You can create guide packs during the trial."
          : "Guide packs and prepaid top-ups unlock when Starter billing starts.",
      },
      {
        icon: "cancel",
        label: "Cancel anytime before billing",
        detail: `Cancel from Pricing or Checkout before ${this.trialChargeDate} and you won't be charged.`,
      },
      {
        icon: "payments",
        label: `Then ${this.trialPriceAfter}`,
        detail: "Full Starter limits (guides, views and AI coins) apply once billing starts.",
      },
    ];
  }

  get checkoutBenefits(): PlanBenefit[] {
    return this.isTrialCheckout ? this.trialUsageRules : this.activePlanBenefits;
  }

  get includedHeading(): string {
    return this.isTrialCheckout ? "Trial usage rules" : "What's included";
  }

  /** The day billing starts if the user doesn't cancel. */
  get trialChargeDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + this.trialDays);
    return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  }

  get activeTrialEndDate(): string {
    return formatTrialEndDate(this.trialStatus);
  }

  get trialPriceAfter(): string {
    return this.isYearlyBilling
      ? `£${this.planCards.starter.annualMonthlyPrice * 12}/yr`
      : `£${this.planCards.starter.monthlyPrice}/mo`;
  }

  onTrialConsentChange(checked: boolean): void {
    this.trialConsent = checked;
  }

  cancelActiveTrial(): void {
    if (this.cancellingTrial) {
      return;
    }
    const confirmed = window.confirm(
      `Cancel your Starter trial? You won't be charged, and creating guides will stop straight away. Guides you've already made are kept.`
    );
    if (!confirmed) {
      return;
    }
    this.cancellingTrial = true;
    this.trialStateService.cancelTrial().subscribe({
      next: () => {
        this.cancellingTrial = false;
        this.trialStatus = this.trialStateService.snapshot;
        this.manageTrial = false;
        this.toastr.success("Your Starter trial has been cancelled. You won't be charged.", "Trial cancelled");
        void this.router.navigateByUrl("/pricing");
      },
      error: (err) => {
        this.cancellingTrial = false;
        this.toastr.error(
          err?.error?.message || "We couldn't cancel your trial. Please try again or contact support.",
          "Cancel failed"
        );
      },
    });
  }

  private loadTrialStatus(): void {
    this.trialStateService.refresh().subscribe((status) => {
      if (this.destroyed) {
        return;
      }
      this.trialStatus = status;
      const wasTrialCheckout = this.isTrialCheckout;
      this.trialEligible = status ? status.eligible : true;
      if (this.trialRequested && status && !status.eligible && !status.active) {
        this.toastr.info(
          "You've already used your Starter free trial. You can still subscribe to Starter.",
          "Free trial unavailable"
        );
      }
      if (wasTrialCheckout !== this.isTrialCheckout) {
        this.selectedPlanId = this.resolvePlanId();
        this.schedulePayPalInit();
      }
    });
  }

  ngOnInit(): void {
    this.loadPlanConfig();
    this.returnUrl = resolveCoinPurchaseReturnUrl(
      this.route.snapshot.queryParamMap.get("returnUrl")
    );
    if (this.returnUrl) {
      setCoinPurchaseReturnUrl(this.returnUrl);
    }

    const userData = this.readLoggedInUser();
    if (!userData) {
      // Stay on a safe page; modal-blur would lock /subscriptions if auth UI fails.
      const returnTarget = this.router.url || "/subscriptions";
      void this.router.navigateByUrl("/pricing").then(() => {
        this.authModalService.openModal("registration", returnTarget);
      });
      return;
    }

    this.checkoutAuthorized = true;
    const email = userData["email"] ?? userData["Email"];
    this.userEmail = typeof email === "string" ? email : "";

    // Apply initial params once from snapshot so first paint is correct.
    this.applyCheckoutQueryParams(this.route.snapshot.queryParamMap, false);

    this.queryParamsSub = this.route.queryParamMap.subscribe((params) => {
      this.applyCheckoutQueryParams(params, true);
    });

    this.loadTrialStatus();
  }

  ngAfterViewInit(): void {
    if (!this.checkoutAuthorized || this.destroyed) {
      return;
    }
    // Defer PayPal so Angular can finish the first paint before SDK DOM work.
    this.paypalScriptTimer = setTimeout(() => {
      this.paypalScriptTimer = null;
      if (!this.destroyed) {
        this.loadPayPalScript();
      }
    }, 300);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.queryParamsSub?.unsubscribe();
    this.queryParamsSub = null;
    if (this.paypalInitTimer) {
      clearTimeout(this.paypalInitTimer);
      this.paypalInitTimer = null;
    }
    if (this.paypalScriptTimer) {
      clearTimeout(this.paypalScriptTimer);
      this.paypalScriptTimer = null;
    }
    this.paypalInitGeneration += 1;
    void this.closePayPalButtonsAsync();
  }

  trackBenefit(_index: number, benefit: PlanBenefit): string {
    return `${benefit.icon}:${benefit.label}`;
  }

  setBilling(annual: boolean): void {
    const billing: CheckoutBilling = annual ? "annual" : "monthly";
    if (this.selectedBilling === billing) {
      return;
    }
    this.navigateCheckoutQueryParams(this.selectedPlan, billing);
  }

  switchPlan(plan: CheckoutPlan): void {
    if (this.selectedPlan === plan) {
      return;
    }
    this.navigateCheckoutQueryParams(plan, this.selectedBilling);
  }

  get planHeading(): string {
    return this.planCards[this.selectedPlan].title;
  }

  get activePlanDescription(): string {
    return this.planCards[this.selectedPlan].description;
  }

  get billingCycleLabel(): string {
    return this.isYearlyBilling ? "Annual billing" : "Monthly billing";
  }

  get pricePerMonth(): number {
    const plan = this.planCards[this.selectedPlan];
    return this.isYearlyBilling ? plan.annualMonthlyPrice : plan.monthlyPrice;
  }

  get totalDueToday(): number {
    if (this.isTrialCheckout) {
      return 0;
    }
    return this.recurringTotal;
  }

  get recurringTotal(): number {
    const plan = this.planCards[this.selectedPlan];
    return this.isYearlyBilling
      ? plan.annualMonthlyPrice * 12
      : plan.monthlyPrice;
  }

  get annualSavings(): number {
    const plan = this.planCards[this.selectedPlan];
    return (plan.monthlyPrice - plan.annualMonthlyPrice) * 12;
  }

  get fullAnnualPriceWithoutDiscount(): number {
    return this.planCards[this.selectedPlan].monthlyPrice * 12;
  }

  get billingNote(): string {
    if (this.isTrialCheckout) {
      return `${this.trialDays}-day free trial, then ${this.trialPriceAfter} starting ${this.trialChargeDate}. Cancel before then and you won't be charged.`;
    }
    if (this.isYearlyBilling) {
      return `£${this.recurringTotal} billed once per year. Equivalent to £${this.pricePerMonth}/mo.`;
    }
    return `£${this.recurringTotal} billed every month. Cancel anytime from your account.`;
  }

  get starterTabPrice(): number {
    const plan = this.planCards.starter;
    return this.isYearlyBilling ? plan.annualMonthlyPrice : plan.monthlyPrice;
  }

  get growthTabPrice(): number {
    const plan = this.planCards.growth;
    return this.isYearlyBilling ? plan.annualMonthlyPrice : plan.monthlyPrice;
  }

  private applyCheckoutQueryParams(
    params: ParamMap,
    allowPayPalRefresh: boolean
  ): void {
    if (this.destroyed) {
      return;
    }

    const plan = (params.get("plan") || "starter").toLowerCase();
    if (plan === "creator") {
      void this.router.navigateByUrl("/pricing");
      return;
    }

    const billing = (params.get("billing") || "monthly").toLowerCase();
    const resolvedReturnUrl = resolveCoinPurchaseReturnUrl(
      params.get("returnUrl")
    );
    if (resolvedReturnUrl) {
      this.returnUrl = resolvedReturnUrl;
      setCoinPurchaseReturnUrl(resolvedReturnUrl);
    }

    const nextPlan: CheckoutPlan = plan === "growth" ? "growth" : "starter";
    const nextBilling: CheckoutBilling =
      billing === "annual" ? "annual" : "monthly";
    const nextTrialRequested = nextPlan === "starter" && params.get("trial") === "1";
    this.manageTrial = params.get("manage") === "trial";
    const planChanged =
      this.selectedPlan !== nextPlan ||
      this.selectedBilling !== nextBilling ||
      this.trialRequested !== nextTrialRequested;
    this.trialRequested = nextTrialRequested;
    if (!nextTrialRequested) {
      this.trialConsent = false;
    }

    this.selectedPlan = nextPlan;
    this.selectedBilling = nextBilling;
    this.isYearlyBilling = nextBilling === "annual";
    this.selectedPlanId = this.resolvePlanId();
    this.activePlanBenefits = this.planBenefits[nextPlan];

    if (allowPayPalRefresh && planChanged) {
      this.schedulePayPalInit();
    }
  }

  private navigateCheckoutQueryParams(
    plan: CheckoutPlan,
    billing: CheckoutBilling
  ): void {
    const queryParams: { [key: string]: string | null } = { plan, billing };
    if (this.returnUrl) {
      queryParams.returnUrl = this.returnUrl;
    }
    // The free trial is Starter-only.
    queryParams.trial = plan === "starter" && this.trialRequested ? "1" : null;

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  private readLoggedInUser(): Record<string, unknown> | null {
    try {
      const raw = localStorage.getItem("LoggedInUser");
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }

  private loadPayPalScript(): void {
    loadPayPalSdk({
      clientId: this.paypalClientId,
      intent: "subscription",
      vault: true,
    })
      .then(() => {
        if (this.destroyed) {
          return;
        }
        this.paypalReady = true;
        this.schedulePayPalInit();
      })
      .catch((error) => {
        console.error("PayPal SDK is not loaded", error);
        this.paypalReady = false;
        if (!this.destroyed) {
          this.toastr.error(
            "PayPal checkout could not be loaded. Please refresh and try again.",
            "Payment unavailable"
          );
        }
      });
  }

  private schedulePayPalInit(): void {
    if (!this.paypalReady || this.destroyed) {
      return;
    }

    if (this.paypalInitTimer) {
      clearTimeout(this.paypalInitTimer);
    }

    this.paypalInitTimer = setTimeout(() => {
      this.paypalInitTimer = null;
      void this.initializePayPalButtons();
    }, 150);
  }

  private async closePayPalButtonsAsync(): Promise<void> {
    const instance = this.paypalButtonsInstance;
    if (!instance) {
      return;
    }

    this.paypalButtonsInstance = null;
    try {
      await Promise.race([
        instance.close(),
        new Promise<void>((resolve) => setTimeout(resolve, 800)),
      ]);
    } catch {
      // Ignore if PayPal already closed the button instance.
    }
  }

  private async initializePayPalButtons(): Promise<void> {
    const generation = ++this.paypalInitGeneration;

    try {
      if (
        this.destroyed ||
        !this.paypalReady ||
        typeof paypal === "undefined"
      ) {
        return;
      }

      const container = document.getElementById(this.paypalContainerId);
      if (!container) {
        return;
      }

      await this.closePayPalButtonsAsync();
      if (generation !== this.paypalInitGeneration || this.destroyed) {
        return;
      }

      container.innerHTML = "";
      const planId = this.resolvePlanId();

      if (!planId) {
        container.innerHTML =
          '<p class="paypal-trial-unavailable">Payment is temporarily unavailable. Please refresh and try again.</p>';
        return;
      }

      const buttons = paypal.Buttons({
        style: {
          shape: "rect",
          color: "gold",
          layout: "vertical",
          label: this.isTrialCheckout ? "paypal" : "subscribe",
        },
        onClick: (_data: unknown, actions: any) => {
          if (this.isTrialCheckout && !this.trialConsent) {
            this.toastr.warning(
              `Please confirm you understand Starter billing starts on ${this.trialChargeDate} unless you cancel.`,
              "Confirmation required"
            );
            return actions.reject();
          }
          return actions.resolve();
        },
        createSubscription: (_data: unknown, actions: any) => {
          // Dedicated PayPal trial plans (TRIAL cycle) when configured; otherwise start the
          // regular Starter plan in the future so nothing is charged today.
          const request: Record<string, unknown> = { plan_id: planId };
          if (this.isTrialCheckout) {
            request.custom_id = "starter_trial";
            if (!this.hasTrialPayPalPlan) {
              request.start_time = this.trialBillingStartIso();
            }
          }
          return actions.subscription.create(request);
        },
        onApprove: (data: { subscriptionID?: string }) => {
          const subscriptionId = data?.subscriptionID;
          if (!subscriptionId) {
            this.toastr.error("PayPal did not return a subscription id.");
            return;
          }
          this.handleSubscriptionSuccess(subscriptionId);
        },
        onError: () => {
          this.toastr.error(
            this.isTrialCheckout
              ? "Free-trial checkout failed. Please try again, or subscribe to Starter without a trial."
              : "PayPal checkout failed. Please try again."
          );
        },
      });

      if (generation !== this.paypalInitGeneration || this.destroyed) {
        await buttons.close().catch(() => undefined);
        return;
      }

      this.paypalButtonsInstance = buttons;
      await buttons.render(`#${this.paypalContainerId}`);
    } catch (error) {
      console.error("Error initializing PayPal buttons:", error);
      if (generation === this.paypalInitGeneration && !this.destroyed) {
        this.paypalButtonsInstance = null;
        this.toastr.error(
          "PayPal button could not be displayed. Please refresh and try again.",
          "Payment unavailable"
        );
      }
    }
  }

  handleSubscriptionSuccess(subscriptionId: string): void {
    this.subscriptionService
      .save({
        paypalSubscriptionId: subscriptionId,
        planId: this.selectedPlanId,
        canonicalPlanId: this.selectedPlan,
        status: "ACTIVE",
        startTimeUtc: new Date().toISOString(),
        isTrial: this.isTrialCheckout ? true : undefined,
      })
      .subscribe({
        next: (result) => {
          const planId =
            result?.effectivePlanId ||
            result?.subscription?.canonicalPlanId ||
            this.selectedPlan;
          const status = (
            result?.subscription?.status || "ACTIVE"
          ).toUpperCase();
          const isActive = status === "ACTIVE" || status === "APPROVED";

          updateLoggedInUser({
            effectivePlanId: planId,
            EffectivePlanId: planId,
          });
          this.trialStateService.refresh().subscribe();

          if (result?.planTier === "trial") {
            this.toastr.success(
              `Your ${this.trialDays}-day Starter trial has started. Create your first guide now.`,
              "Trial started"
            );
            const trialTarget = resolveCoinPurchaseReturnUrl(this.returnUrl);
            clearCoinPurchaseReturnUrl();
            void this.router.navigateByUrl(trialTarget || "/create-guide/manual");
            return;
          }

          if (isActive) {
            this.toastr.success(
              `Payment successful — your ${this.planHeading} subscription is now enabled.`,
              "Success"
            );
          } else {
            this.toastr.info(
              `Payment received. Your ${this.planHeading} subscription is activating…`,
              "Processing"
            );
          }

          const target = resolveCoinPurchaseReturnUrl(this.returnUrl);
          clearCoinPurchaseReturnUrl();
          if (target) {
            void this.router.navigateByUrl(target);
          } else {
            void this.router.navigate(["/user-profile"], {
              queryParams: { showPlan: "1" },
            });
          }
        },
        error: (err) => {
          if (err?.status === 409 && err?.error?.code === "trial_already_used") {
            this.trialEligible = false;
            this.toastr.warning(err.error.message, "Free trial already used");
            this.selectedPlanId = this.resolvePlanId();
            this.schedulePayPalInit();
            return;
          }
          const message =
            err?.error?.error ||
            err?.error?.message ||
            "Payment completed in PayPal, but we could not enable your subscription. Please contact support.";
          this.toastr.error(message, "Error");
        },
      });
  }

  /** ISO start time for deferred Starter billing when no dedicated trial plan exists. */
  private trialBillingStartIso(): string {
    const start = new Date();
    start.setUTCDate(start.getUTCDate() + this.trialDays);
    // PayPal expects second-precision UTC timestamps.
    return start.toISOString().replace(/\.\d{3}Z$/, "Z");
  }

  private resolvePlanId(): string {
    const key = `${this.selectedPlan}_${this.selectedBilling}`;
    if (this.isTrialCheckout) {
      // Prefer a dedicated PayPal plan with a TRIAL cycle when configured.
      const trialPlanId = this.trialPlanMappings[key] || "";
      if (trialPlanId) {
        (window as any).__flowchestSelectedPlanId = trialPlanId;
        return trialPlanId;
      }
      // Otherwise use Starter and defer the first charge with start_time.
      const starterFallback =
        this.planMappings[`starter_${this.selectedBilling}`] ??
        "P-9VU70762PU6215539ND7DKVI";
      (window as any).__flowchestSelectedPlanId = starterFallback;
      return starterFallback;
    }
    const fallback = this.planMappings[key] ?? "P-9VU70762PU6215539ND7DKVI";
    (window as any).__flowchestSelectedPlanId = fallback;
    return fallback;
  }

  private loadPlanConfig(): void {
    this.subscriptionService.getPlanConfig().subscribe({
      next: (config) => {
        if (this.destroyed) {
          return;
        }
        this.planConfig = mergePlanConfig(config);
        this.refreshPlanDisplayData();
      },
      error: () => {
        if (this.destroyed) {
          return;
        }
        this.planConfig = mergePlanConfig();
        this.refreshPlanDisplayData();
      },
    });
  }

  private refreshPlanDisplayData(): void {
    this.planCards = this.buildPlanCards();
    this.planBenefits = this.buildPlanBenefits();
    this.activePlanBenefits = this.planBenefits[this.selectedPlan];
  }

  private buildPlanBenefits(): Record<CheckoutPlan, PlanBenefit[]> {
    const viewOverageDetail =
      "Pay-as-you-go billing when you exceed your included monthly views";
    const guideTopUpDetail =
      "Purchase prepaid guide top-ups when you exceed your included published guides";
    return {
      starter: [
        {
          icon: "menu_book",
          label: starterGuideFeature(this.planConfig),
          detail: "Create guided product experiences for your catalog",
        },
        {
          icon: "format_list_numbered",
          label: `${getPlanLimits(this.planConfig, "starter").maxStepsPerFlow} steps per guide`,
          detail: "Detailed step-by-step instructions per product",
        },
        {
          icon: "visibility",
          label: starterViewsFeature(this.planConfig),
          detail: "Track customer engagement across your guides",
        },
        {
          icon: "auto_awesome",
          label: starterCoinsFeature(this.planConfig),
          detail: "Power Smart Create and AI-assisted content",
        },
        { icon: "menu_book", label: "Guide top-ups", detail: guideTopUpDetail },
        { icon: "payments", label: "View overage", detail: viewOverageDetail },
      ],
      growth: [
        {
          icon: "menu_book",
          label: growthGuideFeature(this.planConfig),
          detail: "Scale guides across your full product range",
        },
        {
          icon: "format_list_numbered",
          label: `${getPlanLimits(this.planConfig, "growth").maxStepsPerFlow} steps per guide`,
          detail: "Richer, more detailed assembly instructions",
        },
        {
          icon: "visibility",
          label: growthViewsFeature(this.planConfig),
          detail: "Handle high-traffic brands and retailers",
        },
        {
          icon: "auto_awesome",
          label: growthCoinsFeature(this.planConfig),
          detail: "Heavy AI usage for content creation at scale",
        },
        { icon: "menu_book", label: "Guide top-ups", detail: guideTopUpDetail },
        { icon: "payments", label: "View overage", detail: viewOverageDetail },
      ],
    };
  }

  private buildPlanCards(): Record<CheckoutPlan, PlanCard> {
    const starter = getPlanLimits(this.planConfig, "starter");
    const growth = getPlanLimits(this.planConfig, "growth");
    const viewOverage = formatViewOverageRate(this.planConfig);

    return {
      starter: {
        title: "Starter",
        description:
          "For early-stage brands and pilot projects launching guided product experiences.",
        monthlyPrice: 99,
        annualMonthlyPrice: 79,
        annualLabel: "billed annually",
        features: [
          `Up to ${starter.maxPublishedFlows} guides / month`,
          `Up to ${starter.maxStepsPerFlow} steps per guide`,
          `Up to ${formatViewsLimit(starter.maxMonthlyViews)} monthly views`,
          `${starter.monthlyCoinAllowance} AI coins per month`,
          `View overage: ${viewOverage}`,
          "Guide top-ups available when you exceed included guides",
        ],
        buttonLabel: "Subscribe to Starter",
      },
      growth: {
        title: "Growth",
        description:
          "For established brands that need more guides, more views, and higher AI usage.",
        monthlyPrice: 399,
        annualMonthlyPrice: 319,
        annualLabel: "billed annually",
        features: [
          `Up to ${growth.maxPublishedFlows} guides / month`,
          `Up to ${growth.maxStepsPerFlow} steps per guide`,
          `Up to ${formatViewsLimit(growth.maxMonthlyViews)} monthly views`,
          `${growth.monthlyCoinAllowance} AI coins per month`,
          `View overage: ${viewOverage}`,
          "Guide top-ups available when you exceed included guides",
        ],
        buttonLabel: "Subscribe to Growth",
        highlighted: true,
      },
    };
  }
}
